import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { GalleryTabContent } from "./GalleryTabContent";
import { UploadPhotoDialog } from "./UploadPhotoDialog";
import { UploadCollectionPhotoDialog } from "./UploadCollectionPhotoDialog";
import { GalleryPdfExport } from "./GalleryPdfExport";
import { GalleryPhoto } from "./carousel/types";

const collections = [
  { title: "First Year", key: "first-year", translationKey: 'gallery.firstYear' },
  { title: "Family Moments", key: "family-moments", translationKey: 'gallery.familyMoments' },
  { title: "Special Events", key: "special-events", translationKey: 'gallery.specialEvents' }
];

interface GalleryTabsProps {
  userId?: string;
  isPremium: boolean;
  isGuestMode?: boolean;
  recentPhotos: GalleryPhoto[];
  milestonePhotos: GalleryPhoto[];
  collectionPhotos: Record<string, GalleryPhoto[]>;
  fetchGalleryPhotos: () => void;
}

export function GalleryTabs({ 
  userId, 
  isPremium, 
  isGuestMode = false,
  recentPhotos,
  milestonePhotos,
  collectionPhotos,
  fetchGalleryPhotos
}: GalleryTabsProps) {
  const [activeTab, setActiveTab] = useState("recent");
  const { language } = useLanguage();
  
  const getActivePhotos = () => {
    if (activeTab === "recent") return recentPhotos;
    if (activeTab === "milestones") return milestonePhotos;
    return collectionPhotos[activeTab] || [];
  };
  
  const getActiveCollectionTitle = () => {
    if (activeTab === "recent") return t('gallery.recentPhotos', language);
    if (activeTab === "milestones") return t('gallery.milestonePhotos', language);
    
    const collection = collections.find(c => c.key === activeTab);
    return collection ? t(collection.translationKey as any, language) || collection.title : '';
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        {isPremium && !isGuestMode && (
          <UploadPhotoDialog userId={userId} onPhotoUploaded={fetchGalleryPhotos} />
        )}
        <div className={isPremium && !isGuestMode ? '' : 'ml-auto'}>
          <GalleryPdfExport 
            photos={getActivePhotos()} 
            collectionTitle={getActiveCollectionTitle()} 
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 flex flex-wrap gap-1">
          <TabsTrigger value="recent">{t('gallery.recentPhotos', language)}</TabsTrigger>
          <TabsTrigger value="milestones">{t('gallery.milestonePhotos', language)}</TabsTrigger>
          
          {collections.map(collection => (
            <TabsTrigger key={collection.key} value={collection.key}>
              {t(collection.translationKey as any, language) || collection.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {isPremium && !isGuestMode && activeTab !== "recent" && activeTab !== "milestones" && (
          <div className="flex justify-end mb-4">
            <UploadCollectionPhotoDialog 
              userId={userId}
              collectionKey={activeTab}
              onPhotoUploaded={fetchGalleryPhotos}
            />
          </div>
        )}

        <GalleryTabContent 
          value="recent" 
          photos={recentPhotos} 
          onPhotoDeleted={fetchGalleryPhotos} 
          isGuestMode={isGuestMode}
        />
        <GalleryTabContent 
          value="milestones" 
          photos={milestonePhotos} 
          onPhotoDeleted={fetchGalleryPhotos}
          isGuestMode={isGuestMode}
        />
        
        {collections.map(collection => (
          <GalleryTabContent 
            key={collection.key}
            value={collection.key}
            photos={collectionPhotos[collection.key] || []}
            title={t(collection.translationKey as any, language) || collection.title}
            onPhotoDeleted={fetchGalleryPhotos}
            isGuestMode={isGuestMode}
          />
        ))}
      </Tabs>
    </>
  );
}
